import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import AnnotationMarker from './AnnotationMarker';
import AnnotationClusterMarker from './AnnotationClusterMarker';
import AnnotationRangeBand from './AnnotationRangeBand';
import RangeClusterPopover from './RangeClusterPopover';
import LoadingSvg from '../../../common/images/LoadingSvg';
import { getAnnotationColor } from '../utils/annotationPresentation';
import { getAnnotationTypeLabel } from '../utils/annotationDescriptionDisplay';
import { groupByProximity } from '../utils/clusterUtils';
import {
  RANGE_TIME_FORMAT,
  isRangeAnnotation,
  buildRangeLanes,
} from '../utils/annotationRange';
import {
  OverlayContainer,
  LoadingContainer,
  AnnotationMarkers,
  VerticalDottedLine,
  CreateCaptureLayer,
  DragSelectionBand,
  HoverGuideLine,
  HoverTimeLabel,
  RangeBandTint,
  LaneLabel,
  LaneTrack,
  RangeBadge,
} from './AnnotationOverlay.styled';

// Range-lane layout (px). One fixed-height lane per range-capable type is
// stacked from the top of the plot; point-in-time flags sit on a single row
// just beneath the lanes.
const RANGE_LANE_H = 9; // pill / lane height
const RANGE_LANE_GAP = 6; // vertical gap between lanes
const RANGE_LANE_TOP = 6; // top offset of the first lane
const FLAG_ROW_GAP = 6; // gap between the last lane and the flag row

// Pixels of horizontal movement below which a drag is treated as a click
// (point-in-time) rather than a time-range selection.
const DRAG_THRESHOLD_PX = 4;

function getColor(annotation) { return getAnnotationColor(annotation); }

function AnnotationOverlay(props) {
  const { t } = useTranslation();
  const {
    annotations = [],
    loading = false,
    showControls = true,
    onAnnotationCreate,
    enableMarkers = true,
    enableDottedLines = true,
    onAnnotationClick,
    createMode = false,
    timeRange,
    domain
  } = props;

  // All time<->x math maps against the chart's LIVE visible domain when the
  // trend chart reports one (zoom/pan), falling back to the static timeRange.
  // Using the static range here would save notes at the wrong timestamp and
  // misplace the hover guide whenever the user has zoomed/panned.
  const activeRange = useMemo(() => (
    (domain && domain.start && domain.end) ? domain : timeRange
  ), [domain, timeRange]);

  const markersRef = useRef(null);
  const captureRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [plotHeight, setPlotHeight] = useState(null);
  const [drag, setDrag] = useState(null);
  const [hover, setHover] = useState(null);
  // Open ×N range picker ({ items, type, x, y }) and the span it spotlights.
  const [rangePopover, setRangePopover] = useState(null);
  const [spotlight, setSpotlight] = useState(null);

  const measureWidth = useCallback(() => {
    const anchor = markersRef.current || captureRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = Math.max(0, Math.floor(rect.width));
    setContainerWidth(prev => (prev !== width ? width : prev));

    // Measure the chart plot height so overlay elements don't spill below it.
    // The overlay container is one child of the chart wrapper; the chart is its
    // sibling. Pick the TALLEST non-overlay sibling rather than the first one
    // with any height: on the compact layout the first sibling can be a short
    // header/legend row, which under-measures the plot and clips the full-height
    // span tint above the data. The plot is the tallest content in the wrapper,
    // so max height is a robust proxy that still degrades to the full-height
    // fallback if nothing measurable is found.
    try {
      const overlayEl = anchor.parentElement;
      const wrapper = overlayEl && overlayEl.parentElement;
      const siblings = wrapper ? Array.prototype.slice.call(wrapper.children) : [];
      let maxHeight = 0;
      siblings.forEach((child) => {
        if (child === overlayEl) return;
        // Skip fixed-position siblings: NoteCreationModal renders its
        // full-viewport `position: fixed` backdrop inline in the same
        // ChartWrapper (no portal), so while a note is being composed it would
        // otherwise win the max and balloon plotHeight to viewport height. The
        // plot is never fixed-positioned.
        const pos = window.getComputedStyle(child).position;
        if (pos === 'fixed') return;
        const h = child.getBoundingClientRect().height;
        if (h > maxHeight) maxHeight = h;
      });
      if (maxHeight > 0) {
        const ph = Math.round(maxHeight);
        setPlotHeight(prev => (prev !== ph ? ph : prev));
      }
    } catch (e) {
      // leave plotHeight as-is; styled components fall back to full height
    }
  }, []);

  useEffect(() => {
    measureWidth();
    window.addEventListener('resize', measureWidth);
    return () => window.removeEventListener('resize', measureWidth);
  }, [measureWidth]);

  // Re-measure when overlay content/state changes (data load, mode toggle).
  useEffect(() => { measureWidth(); }, [measureWidth, createMode, annotations, timeRange]);

  const plotHeightCss = plotHeight ? `${plotHeight}px` : undefined;

  const getAnnotationPosition = useCallback((annotation) => {
    if (!activeRange || !activeRange.start || !activeRange.end) {
      return { x: '0%', y: '50%' };
    }
    try {
      const startTime = new Date(activeRange.start);
      const endTime = new Date(activeRange.end);
      const annotationTime = new Date(annotation.start_time);
      const timespan = endTime.getTime() - startTime.getTime();
      if (!Number.isFinite(timespan) || timespan === 0) {
        return { x: '0%', y: '50%' };
      }
      const relativeTime = annotationTime.getTime() - startTime.getTime();
      const xPercent = Math.max(0, Math.min(100, (relativeTime / timespan) * 100));
      // y is set by the renderer (all flags share one row beneath the lanes).
      return { x: `${xPercent}%`, y: '50%' };
    } catch (e) {
      return { x: '0%', y: '50%' };
    }
  }, [activeRange]);

  // Map a time (Date/ISO string) to a horizontal percent within the chart,
  // unclamped: a time outside the visible range yields <0 or >100. Only
  // buildRangeLanes wants this, so it can tell a range that genuinely ends at
  // the window edge from one that merely got clamped there.
  //
  // This path uses the overflow only to DROP ranges that fall outside the
  // window; it renders no continues-beyond affordance from the resulting
  // continuesBefore/continuesAfter flags, unlike RangeLaneBand. That is
  // deliberate: the overlay draws no boundary ticks, so a clamped pill here
  // makes no false claim about where the range ends -- there is nothing
  // misleading to correct.
  const getRawXPercentForTime = useCallback((time) => {
    if (!activeRange || !activeRange.start || !activeRange.end) return null;
    const startTime = new Date(activeRange.start).getTime();
    const endTime = new Date(activeRange.end).getTime();
    const span = endTime - startTime;
    if (!Number.isFinite(span) || span === 0) return null;
    const tMs = new Date(time).getTime();
    if (!Number.isFinite(tMs)) return null;
    return ((tMs - startTime) / span) * 100;
  }, [activeRange]);

  // Clamped variant: positions that must land inside the plot box.
  const getXPercentForTime = useCallback((time) => {
    const raw = getRawXPercentForTime(time);
    return raw === null ? null : Math.max(0, Math.min(100, raw));
  }, [getRawXPercentForTime]);

  // Map a horizontal fraction (0..1) within the chart to an ISO timestamp.
  const timeAtFraction = useCallback((fraction) => {
    if (!activeRange || !activeRange.start || !activeRange.end) return null;
    const startTime = new Date(activeRange.start).getTime();
    const endTime = new Date(activeRange.end).getTime();
    const span = endTime - startTime;
    if (!Number.isFinite(span)) return null;
    const clamped = Math.max(0, Math.min(1, fraction));
    return new Date(startTime + (clamped * span)).toISOString();
  }, [activeRange]);

  const relativeX = useCallback((clientX) => {
    if (!captureRef.current) return { x: 0, width: 0 };
    const rect = captureRef.current.getBoundingClientRect();
    const width = Math.max(0, rect.width);
    const x = Math.max(0, Math.min(width, clientX - rect.left));
    return { x, width };
  }, []);

  // Read by the window/move handlers so they don't re-subscribe on every move.
  const dragRef = useRef(null);
  useEffect(() => { dragRef.current = drag; }, [drag]);

  const handleCaptureMouseDown = useCallback((event) => {
    if (!createMode) return;
    // Only a primary (left) press starts a note gesture; a right-click should
    // still reach the browser context menu / existing chart handlers.
    if (event.button !== 0) return;
    const { x, width } = relativeX(event.clientX);
    setDrag({ startX: x, currentX: x, width });
  }, [createMode, relativeX]);

  const handleCaptureMouseMove = useCallback((event) => {
    const { x, width } = relativeX(event.clientX);
    // Always track the cursor so we can show a timestamp guide for reference.
    const frac = width ? x / width : 0;
    setHover({ x, frac, time: width ? timeAtFraction(frac) : null });
    if (dragRef.current) {
      setDrag(prev => (prev ? { ...prev, currentX: x, width } : prev));
    }
  }, [relativeX, timeAtFraction]);

  const handleCaptureMouseUp = useCallback((event) => {
    // Mirror the mousedown guard: only a primary (left) release commits the note.
    // A right-button release mid left-drag must not commit or open the modal.
    if (event.button !== 0) return;
    const drag = dragRef.current;
    if (!drag) return;
    const { x, width } = relativeX(event.clientX);
    setDrag(null);
    if (!width || !onAnnotationCreate) return;

    const lo = Math.min(drag.startX, x);
    const hi = Math.max(drag.startX, x);
    const isPoint = (hi - lo) < DRAG_THRESHOLD_PX;

    const startTime = timeAtFraction(lo / width);
    const endTime = isPoint ? null : timeAtFraction(hi / width);
    if (!startTime) return;

    onAnnotationCreate({
      startTime,
      endTime,
      clientX: event.clientX,
      clientY: event.clientY
    });
  }, [relativeX, timeAtFraction, onAnnotationCreate]);

  const handleCaptureMouseLeave = useCallback(() => {
    // Keep an in-progress drag alive when the pointer leaves the layer; a
    // window-level mouseup (below) still commits it. Only drop the hover guide.
    setHover(null);
  }, []);

  // Commit a drag even when released outside the capture layer. Bound
  // continuously (the handler no-ops without an active drag) to avoid
  // re-subscribing on every mousemove.
  useEffect(() => {
    window.addEventListener('mouseup', handleCaptureMouseUp);
    return () => window.removeEventListener('mouseup', handleCaptureMouseUp);
  }, [handleCaptureMouseUp]);

  const groups = useMemo(() => {
    // Skip the O(n log n) clustering when nothing renders it (the trend chart
    // disables both markers and drop-lines) rather than recomputing per tick.
    if (!enableMarkers && !enableDottedLines) return [];
    const prepared = (annotations || []).map((a) => {
      const position = getAnnotationPosition(a);
      const xPercent = typeof position.x === 'string' ? parseFloat(position.x) : position.x;
      const yPercent = typeof position.y === 'string' ? parseFloat(position.y) : position.y;
      return {
        ref: a,
        item: a,
        position,
        xPercent: Number.isFinite(xPercent) ? xPercent : 0,
        xPx: containerWidth * ((Number.isFinite(xPercent) ? xPercent : 0) / 100),
        yPercent: Number.isFinite(yPercent) ? yPercent : 50,
        color: getColor(a)
      };
    });
    const rawGroups = groupByProximity(prepared, containerWidth);
    return rawGroups.map(g => ({
      key: g.key,
      xPercent: g.xPercent,
      yPercent: g.yPercent,
      clusterColor: g.color || '#1a73e8',
      items: g.items
    }));
  }, [annotations, containerWidth, getAnnotationPosition, enableMarkers, enableDottedLines]);

  // Range annotations grouped into one fixed-height lane per type. Within a lane
  // every range is its own translucent pill (density by opacity); overlapping
  // pills are also gathered into ×N badges that open a picker. Lanes never stack
  // into extra rows, so the footprint stays constant as volume grows.
  const rangeLanes = useMemo(() => {
    // Rendered only under enableDottedLines; skip the layout pass otherwise.
    if (!enableDottedLines) return [];
    // Fuse sub-2px slivers (converted to percent) without bridging real gaps.
    const gap = containerWidth ? (2 / containerWidth) * 100 : 0;
    return buildRangeLanes(annotations, getRawXPercentForTime, { gap }).map(
      (lane, idx) => ({
        ...lane,
        top: RANGE_LANE_TOP + (idx * (RANGE_LANE_H + RANGE_LANE_GAP))
      })
    );
  }, [annotations, getRawXPercentForTime, containerWidth, enableDottedLines]);

  // Vertical geometry derived from the lane count: flags share a single row just
  // beneath the lanes, and their drop-lines fall from there to the plot floor.
  const lanesBottom = RANGE_LANE_TOP + (rangeLanes.length * (RANGE_LANE_H + RANGE_LANE_GAP));
  const flagRowCenter = lanesBottom + FLAG_ROW_GAP + 8;
  const dropLineTop = flagRowCenter + 6;
  const dropLineHeight = plotHeight ? `${Math.max(0, plotHeight - dropLineTop)}px` : plotHeightCss;

  const pillSize = plotHeight && plotHeight < 160 ? 16 : 18;

  const handleMarkerClick = useCallback((annotation, event) => {
    if (onAnnotationClick) onAnnotationClick(annotation, event);
  }, [onAnnotationClick]);

  // ×N badge → floating picker listing every range in the overlap zone.
  const openRangePopover = useCallback((items, type, event) => {
    const margin = 12;
    const halfWidth = 170;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const clientX = (event && event.clientX) || vw / 2;
    const clientY = (event && event.clientY) || 0;
    const x = Math.min(Math.max(clientX, margin + halfWidth), vw - margin - halfWidth);
    setSpotlight(null);
    setRangePopover({ items, type, x, y: clientY + 8 });
  }, []);
  const closeRangePopover = useCallback(() => { setRangePopover(null); setSpotlight(null); }, []);
  const spotlightItem = useCallback((a) => {
    const s = getXPercentForTime(a.start_time);
    const e = getXPercentForTime(a.end_time || a.start_time);
    if (s === null || e === null) return;
    setSpotlight({
      left: Math.min(s, e),
      width: Math.abs(e - s),
      color: getAnnotationColor(a, { respectSeverity: false })
    });
  }, [getXPercentForTime]);
  const clearSpotlight = useCallback(() => setSpotlight(null), []);

  const isDraggingRange = drag && Math.abs(drag.currentX - drag.startX) >= DRAG_THRESHOLD_PX;
  let dragGeom = null;
  if (isDraggingRange) {
    const lo = Math.min(drag.startX, drag.currentX);
    const hi = Math.max(drag.startX, drag.currentX);
    const w = drag.width || 0;
    dragGeom = {
      lo,
      hi,
      startTime: w ? timeAtFraction(lo / w) : null,
      endTime: w ? timeAtFraction(hi / w) : null
    };
  }

  const captureLayer = createMode ? (
    <CreateCaptureLayer
      innerRef={(el) => { captureRef.current = el; }}
      height={plotHeightCss}
      onMouseDown={handleCaptureMouseDown}
      onMouseMove={handleCaptureMouseMove}
      onMouseLeave={handleCaptureMouseLeave}
    >
      {isDraggingRange && (
        <>
          <DragSelectionBand
            left={`${dragGeom.lo}px`}
            width={`${dragGeom.hi - dragGeom.lo}px`}
          />
          {dragGeom.startTime && (
            <HoverTimeLabel left={`${dragGeom.lo}px`} alignRight>
              {moment(dragGeom.startTime).format(RANGE_TIME_FORMAT)}
            </HoverTimeLabel>
          )}
          {dragGeom.endTime && (
            <HoverTimeLabel left={`${dragGeom.hi}px`}>
              {moment(dragGeom.endTime).format(RANGE_TIME_FORMAT)}
            </HoverTimeLabel>
          )}
        </>
      )}
      {hover && hover.time && !drag && (
        <>
          <HoverGuideLine left={`${hover.x}px`} />
          <HoverTimeLabel
            left={`${hover.x}px`}
            alignRight={hover.frac > 0.8}
          >
            {moment(hover.time).format(RANGE_TIME_FORMAT)}
          </HoverTimeLabel>
        </>
      )}
    </CreateCaptureLayer>
  ) : null;

  if (loading) {
    return (
      <OverlayContainer>
        <LoadingContainer>
          <LoadingSvg width="20" height="20" />
          <span>{t('annotations.overlay.loading')}</span>
        </LoadingContainer>
      </OverlayContainer>
    );
  }

  if (!annotations || annotations.length === 0) {
    return (
      <OverlayContainer>
        {/* No annotations to show, but still allow creating them in add-note mode */}
        {captureLayer}
      </OverlayContainer>
    );
  }

  return (
    <OverlayContainer>
      {/* Faint span tint shown while hovering a range pill or a popover row. */}
      {spotlight && (
        <RangeBandTint
          left={`${spotlight.left}%`}
          width={`${spotlight.width}%`}
          color={spotlight.color}
          height={plotHeightCss}
        />
      )}

      {/* One lane per range type: track + density pills + ×N badges + label. */}
      {enableDottedLines && rangeLanes.map(lane => (
        <React.Fragment key={`lane-${lane.type}`}>
          <LaneTrack
            top={`${lane.top}px`}
            left={`${lane.trackLeft}%`}
            width={`${lane.trackWidth}%`}
            color={lane.color}
            barHeight={`${RANGE_LANE_H}px`}
          />
          {lane.pills.map(pill => (
            <AnnotationRangeBand
              key={`pill-${pill.ref.id}`}
              annotation={pill.ref}
              left={`${pill.left}%`}
              width={`${pill.width}%`}
              color={lane.color}
              top={`${lane.top}px`}
              barHeight={`${RANGE_LANE_H}px`}
              plotHeight={plotHeightCss}
              showControls={showControls}
              onClick={handleMarkerClick}
            />
          ))}
          {lane.badges.map(badge => (
            <RangeBadge
              key={badge.key}
              type="button"
              top={`${lane.top + (RANGE_LANE_H / 2)}px`}
              left={`${badge.center}%`}
              color={lane.color}
              aria-haspopup="menu"
              aria-expanded={
                !!rangePopover
                && rangePopover.type === lane.type
                && rangePopover.items === badge.items
              }
              aria-label={t('annotations.overlay.rangeBadge', {
                defaultValue: '{{count}} overlapping notes',
                count: badge.count
              })}
              onClick={(e) => { e.stopPropagation(); openRangePopover(badge.items, lane.type, e); }}
            >
              {`×${badge.count} ▾`}
            </RangeBadge>
          ))}
          <LaneLabel top={`${lane.top + (RANGE_LANE_H / 2)}px`} color={lane.color}>
            {`${getAnnotationTypeLabel(t, lane.type)} ${lane.count}`}
          </LaneLabel>
        </React.Fragment>
      ))}

      <AnnotationMarkers innerRef={(el) => { if (markersRef) markersRef.current = el; }}>
        {enableMarkers && groups.map((group) => {
          if (group.items.length === 1) {
            const { item, color } = group.items[0];
            return (
              <AnnotationMarker
                key={item.id}
                annotation={item}
                position={{ x: `${group.xPercent}%`, y: `${flagRowCenter}px` }}
                color={color}
                onClick={event => handleMarkerClick(item, event)}
                showControls={showControls}
              />
            );
          }

          return (
            <AnnotationClusterMarker
              key={`cluster-${group.key}`}
              position={{ x: `${group.xPercent}%`, y: `${flagRowCenter}px` }}
              count={group.items.length}
              items={group.items.map(i => i.item)}
              getItemColor={a => getColor(a)}
              onItemClick={(a, e) => handleMarkerClick(a, e)}
              color={group.clusterColor}
              size={pillSize}
            />
          );
        })}

        {enableDottedLines && groups.map((group) => {
          const first = group.items[0];
          if (!first) return null;
          // Mirror the range filter exactly: anything that is not a range band
          // (incl. tasks/repairs that happen to carry an end_time) is a point.
          if (isRangeAnnotation(first.item)) return null;
          const color = first.color;
          return (
            <VerticalDottedLine
              key={`line-${group.key}`}
              left={`${group.xPercent}%`}
              color={color}
              top={`${dropLineTop}px`}
              height={dropLineHeight}
            />
          );
        })}
      </AnnotationMarkers>

      {rangePopover && (
        <RangeClusterPopover
          items={rangePopover.items}
          type={rangePopover.type}
          position={{ x: rangePopover.x, y: rangePopover.y }}
          onSelect={(a) => { closeRangePopover(); handleMarkerClick(a); }}
          onClose={closeRangePopover}
          onHoverItem={spotlightItem}
          onLeaveItem={clearSpotlight}
        />
      )}

      {captureLayer}
    </OverlayContainer>
  );
}

AnnotationOverlay.propTypes = {
  annotations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    start_time: PropTypes.string.isRequired,
    end_time: PropTypes.string,
    description: PropTypes.string,
    severity: PropTypes.oneOf(['low', 'medium', 'high', 'critical']),
    created_by: PropTypes.number,
    created_at: PropTypes.string
  })),

  timeRange: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    end: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
  }),
  // Live (zoom/pan-aware) visible domain; preferred over timeRange for all
  // time<->x mapping when present. ms numbers, ISO strings, or Dates.
  domain: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
    end: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)])
  }),

  loading: PropTypes.bool,
  // chartHeight and chartWidth are accepted to match wrapper API but unused here
  showControls: PropTypes.bool,

  onAnnotationClick: PropTypes.func,
  onAnnotationCreate: PropTypes.func,
  enableMarkers: PropTypes.bool,
  enableDottedLines: PropTypes.bool,
  createMode: PropTypes.bool
};

AnnotationOverlay.defaultProps = {
  annotations: [],
  timeRange: null,
  domain: null,
  loading: false,
  showControls: true,
  onAnnotationClick: null,
  onAnnotationCreate: null,
  enableMarkers: true,
  enableDottedLines: true,
  createMode: false
};

export default AnnotationOverlay;
